import { Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/auth';
import { KbTreeProvider } from './lib/kb-tree';
import { ProjectTreeProvider, useProjectTree } from './lib/project-tree';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { AppShell } from './components/layout/AppShell';
import { ProjectSettingsLayout } from './components/layout/ProjectSettingsLayout';
import { AdminRoute } from './components/admin/AdminRoute';
import AdminFeatures from './pages/admin/AdminFeatures';
import AdminFeedback from './pages/admin/AdminFeedback';
import AdminPlayground from './pages/admin/AdminPlayground';
import AdminMetrics from './pages/admin/AdminMetrics';
import AdminGuardrail from './pages/admin/AdminGuardrail';
import AdminProjectAccess from './pages/admin/AdminProjectAccess';
import AdminUsers from './pages/admin/AdminUsers';
import Automations from './pages/Automations';
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Docs from './pages/Docs';
import Kb from './pages/Kb';
import KbReview from './pages/KbReview';
import KbSweeps from './pages/KbSweeps';
import KbSearch from './pages/KbSearch';
import Feed from './pages/Feed';
import Chats from './pages/Chats';
import Spaces from './pages/Spaces';
import Integrations from './pages/Integrations';
import Sources from './pages/Sources';
import IngestRules from './pages/IngestRules';
import ProjectAccess from './pages/ProjectAccess';
import ProjectSettings from './pages/ProjectSettings';
import AddToKb from './pages/AddToKb';

/**
 * Shell for one project's knowledge base.
 *
 * The page tree is owned here, above the KB page, so the pages panel that draws it and the
 * document view that edits it read one tree — and so it survives moving between pages of the
 * same project instead of being refetched, and shown loading, on every click. It is deliberately
 * *not* part of the app sidebar: that is the client/project hierarchy people navigate by, and a
 * project's pages are a view inside the project rather than a level of that hierarchy.
 *
 * It carries the `/p/:projectSlug` path itself because the provider resolves the project from
 * that param, and a pathless layout route would match with no params at all.
 */
function KbWorkspace() {
  return (
    <KbTreeProvider>
      <Outlet />
    </KbTreeProvider>
  );
}

/**
 * Where a bare "/" — or any unknown URL — lands.
 *
 * The organization is the root of the hierarchy and the one project guaranteed to exist, so it
 * replaces the all-projects cards page the app used to fall back to. Navigation now happens in
 * the sidebar tree, which is present on every project-scoped page.
 */
function OrganizationHome() {
  const { organization, loading, error, reload } = useProjectTree();
  const { logout } = useAuth();

  if (loading) return null;
  if (organization) return <Navigate to={`/p/${organization.slug}`} replace />;

  // Deliberately *not* `<Navigate to="/login">`: ProtectedRoute has already established a session
  // by the time this renders, so redirecting on an empty or failed tree fetch drops a signed-in
  // user back onto the sign-in form with nothing explaining why — which from their side is
  // indistinguishable from the login not having worked at all.
  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-6">
      <div className="max-w-sm text-center">
        <p className="text-sm font-medium text-ink-secondary mb-1.5">
          {error ? 'Couldn’t load your projects' : 'No projects yet'}
        </p>
        <p className="text-xs text-ink-faint mb-4">
          {error
            ? error
            : 'Your account is not a member of any project yet. An administrator needs to add you to one.'}
        </p>
        <div className="flex items-center justify-center gap-4">
          {error && (
            <button onClick={reload} className="text-[11px] text-orange-500 hover:text-orange-700 transition-colors">
              Try again
            </button>
          )}
          <button onClick={logout} className="text-[11px] text-ink-faint hover:text-ink-muted transition-colors">
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Keeps the old `/p/:slug/integrations` and `/p/:slug/access` URLs working now that both live a
 * level deeper. A component rather than a plain `<Navigate>` because the project has to survive
 * the move — these went down into settings, not up to the organization.
 */
function SettingsRedirect({ tab }: { tab: 'integrations' | 'access' }) {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  return <Navigate to={`/p/${projectSlug}/settings/${tab}`} replace />;
}

/**
 * Every project-scoped route names the UI feature that gates it, matching UI_FEATURES in
 * @repo/shared-types. A user with that feature switched off is redirected to their first
 * enabled section — see ProtectedRoute.
 */
export default function App() {
  return (
    <AuthProvider>
      {/* Above <Routes>: the project hierarchy *is* the navigation, so it has to outlive the
          thing being navigated. Mounting it per-route would refetch the whole tree on every
          project switch. */}
      <ProjectTreeProvider>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<Login />} />
        {/* Accounts are made by a first Auth0 sign-in now; old links land on the one way in. */}
        <Route path="/register" element={<Navigate to="/login" replace />} />

        {/* Also public, and deliberately so: the documentation is instructions *about* the product,
            written to be shareable with someone who has no account yet — and the one thing that must
            still render for a user whose sections have all been switched off, since that is exactly
            when they need to be told how any of this works. It carries no feature flag for the same
            reason, and its content is curated by hand rather than generated from the app (see
            src/docs). */}
        <Route path="/doc" element={<Docs />} />
        <Route path="/doc/:slug" element={<Docs />} />

        {/* No all-projects page any more: the sidebar tree is how you move between projects,
            so "/" resolves to the organization at the root of that tree. */}
        <Route path="/" element={<ProtectedRoute><OrganizationHome /></ProtectedRoute>} />

        {/* System administration. Gated on the isSystemAdmin flag client-side for presentation;
            every /admin API route is independently guarded server-side. Feature flags do not
            apply here — this area is governed by that flag alone. */}
        <Route path="/admin" element={<Navigate to="/admin/features" replace />} />
        <Route path="/admin/features" element={<AdminRoute><AdminFeatures /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
        <Route path="/admin/access" element={<AdminRoute><AdminProjectAccess /></AdminRoute>} />
        <Route path="/admin/metrics" element={<AdminRoute><AdminMetrics /></AdminRoute>} />
        <Route path="/admin/feedback" element={<AdminRoute><AdminFeedback /></AdminRoute>} />
        <Route path="/admin/guardrail" element={<AdminRoute><AdminGuardrail /></AdminRoute>} />
        {/* The agent playground. Admin-only for three reasons, none of them secrecy: a run spends
            real money on a flagship model, the candidate list it reads is the entire hierarchy —
            crossing every membership boundary the rest of the API enforces — and changing a tool
            roster is something you do to find out what breaks. */}
        <Route path="/admin/playground" element={<AdminRoute><AdminPlayground /></AdminRoute>} />

        {/* Everything that carries the app sidebar lives under one pathless shell route, so the
            sidebar — its scroll position, and which branches of the hierarchy are collapsed —
            survives navigation instead of being remounted by every page. See AppShell. */}
        <Route element={<ProtectedRoute><AppShell /></ProtectedRoute>}>

        {/* Org-level, outside /p/:projectSlug on purpose: the destination is an output of this
            page rather than an input to it. */}
        <Route path="/add" element={<ProtectedRoute><AddToKb /></ProtectedRoute>} />

        {/* Also org-level: an automation reads across the hierarchy and is only *homed* at a
            project, so it is managed centrally rather than per project. */}
        <Route path="/automations" element={<ProtectedRoute feature="AUTOMATIONS"><Automations /></ProtectedRoute>} />

        {/* Org-level for the same reason /add is: these sources have no project, and which projects
            receive their content is an output of the router rather than an input to the page. The
            project-scoped counterpart is /p/:projectSlug/integrations below. */}
        <Route path="/sources" element={<ProtectedRoute feature="GLOBAL_INTEGRATIONS"><Sources /></ProtectedRoute>} />
        <Route path="/ingest-rules" element={<ProtectedRoute feature="INGEST_RULES"><IngestRules /></ProtectedRoute>} />
        <Route path="/integrations" element={<Navigate to="/sources" replace />} />

        {/* Project-scoped workspace — the project is picked in the sidebar hierarchy tree and
            every section below stays scoped to it */}
        {/* Landing view for a project: the overview dashboard. It is not a gateable section —
            it has no UI feature flag — because it is where every project link lands and the
            sidebar navigates out from. */}
        <Route path="/p/:projectSlug" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

        {/* Reading across the hierarchy, so addressed without a project: each of these takes the
            project as a *filter* (see ScopeBar), defaulting to the whole organization. Nesting them
            under /p/:projectSlug made the URL claim they were about one project, and made looking at
            two projects at once impossible — which is exactly what a routed pipeline, spreading one
            meeting across several projects, produces work worth looking at. */}
        <Route path="/kb-search" element={<ProtectedRoute feature="KB_ASK"><KbSearch /></ProtectedRoute>} />
        <Route path="/kb-review" element={<ProtectedRoute feature="KB_REVIEW"><KbReview /></ProtectedRoute>} />
        <Route path="/kb-sweeps" element={<ProtectedRoute feature="KB_QUALITY"><KbSweeps /></ProtectedRoute>} />
        {/* The page this replaced. A bookmark strands nobody. */}
        <Route path="/kb-quality" element={<Navigate to="/kb-sweeps" replace />} />
        <Route path="/activity" element={<ProtectedRoute feature="ACTIVITY"><KbReview view="runs" /></ProtectedRoute>} />
        <Route path="/feed" element={<ProtectedRoute feature="FEED"><Feed /></ProtectedRoute>} />
        <Route path="/chats" element={<ProtectedRoute feature="CHATS"><Chats /></ProtectedRoute>} />
        {/* Org-level because a space is the person's, not a project's. Not part of the knowledge base:
            nothing in it is ingested or read by an agent unless its owner asks their own MCP client to. */}
        <Route path="/spaces" element={<ProtectedRoute feature="SPACES"><Spaces /></ProtectedRoute>} />

        {/* Browsing the tree stays project-scoped — a node is addressed within one project — and
            keeps the KbWorkspace shell that owns the page tree. The node route is a splat because
            a path (`engineering/auth/sessions`) contains slashes, unlike every other param here. */}
        <Route path="/p/:projectSlug" element={<KbWorkspace />}>
          <Route path="kb" element={<ProtectedRoute feature="KB"><Kb /></ProtectedRoute>} />
          <Route path="kb/*" element={<ProtectedRoute feature="KB"><Kb /></ProtectedRoute>} />
        </Route>
        {/* Everything that configures one project, under one shell. The shell itself is ungated,
            like the project Overview: General is where a project is renamed, moved and deleted, so
            hiding it behind a feature flag could strand a project with no way to manage it. The two
            tabs keep their own flags.

            Nested here rather than sitting beside each other at the project root because these are
            the last project-scoped sections left — every other one now reads across the hierarchy —
            and as flat sidebar entries they only ever meant "the project you happen to be in". They
            are entered from the project tree's row menu instead, which names its subject. */}
        <Route path="/p/:projectSlug/settings" element={<ProtectedRoute><ProjectSettingsLayout /></ProtectedRoute>}>
          <Route index element={<ProjectSettings />} />
          <Route path="integrations" element={<ProtectedRoute feature="INTEGRATIONS"><Integrations /></ProtectedRoute>} />
          <Route path="access" element={<ProtectedRoute feature="ACCESS"><ProjectAccess /></ProtectedRoute>} />
        </Route>

        </Route>

        {/* These lived under the project prefix until the sections became org-level. Redirected
            rather than dropped, because they are in browser histories and in links people pasted to
            each other. */}
        <Route path="/p/:projectSlug/feed" element={<Navigate to="/feed" replace />} />
        <Route path="/p/:projectSlug/kb-review" element={<Navigate to="/kb-review" replace />} />
        <Route path="/p/:projectSlug/activity" element={<Navigate to="/activity" replace />} />
        <Route path="/p/:projectSlug/kb-search" element={<Navigate to="/kb-search" replace />} />

        {/* Same, for the two that moved *down* into settings rather than up to the organization —
            so they keep their project, which a static <Navigate> could not. */}
        <Route path="/p/:projectSlug/integrations" element={<SettingsRedirect tab="integrations" />} />
        <Route path="/p/:projectSlug/access" element={<SettingsRedirect tab="access" />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </ProjectTreeProvider>
    </AuthProvider>
  );
}
